<script setup lang="ts">
interface Props {
    seed: string;
}

const props = defineProps<Props>();

function hashSeed(value: string): number {
    let hash = 2166136261;
    for (const character of String(value || 'vuey')) {
        hash ^= character.charCodeAt(0);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

function seededRandom(seed: number) {
    let state = seed;
    return () => {
        state += 0x6D2B79F5;
        let value = state;
        value = Math.imul(value ^ (value >>> 15), value | 1);
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
}

const artworkStyle = computed(() => {
    const random = seededRandom(hashSeed(props.seed));
    const lightPalette = [
        'var(--brand-entity-artwork-1)',
        'var(--brand-entity-artwork-2)',
        'var(--brand-entity-artwork-3)',
        'var(--brand-entity-artwork-4)',
        'var(--brand-entity-artwork-5)',
        'var(--brand-entity-artwork-6)',
    ];
    const darkPalette = [
        'var(--brand-dark-entity-artwork-1)',
        'var(--brand-dark-entity-artwork-2)',
        'var(--brand-dark-entity-artwork-3)',
        'var(--brand-dark-entity-artwork-4)',
        'var(--brand-dark-entity-artwork-5)',
        'var(--brand-dark-entity-artwork-6)',
    ];
    const firstIndex = Math.floor(random() * lightPalette.length);
    let secondIndex = Math.floor(random() * lightPalette.length);
    if (secondIndex === firstIndex) secondIndex = (secondIndex + 1) % lightPalette.length;
    const mix = 24 + Math.round(random() * 54);
    return {
        '--entity-artwork-light-background': `color-mix(in srgb, color-mix(in srgb, ${lightPalette[firstIndex]} ${mix}%, ${lightPalette[secondIndex]}) 78%, var(--brand-surface))`,
        '--entity-artwork-dark-background': `color-mix(in srgb, color-mix(in srgb, ${darkPalette[firstIndex]} ${mix}%, ${darkPalette[secondIndex]}) 72%, var(--brand-dark-surface))`,
    };
});

const patternVariant = computed(() => (hashSeed(`${props.seed}-pattern`) % 6) + 1);
</script>

<template>
    <div
        :class="['entity-artwork relative isolate h-full w-full overflow-hidden', `entity-artwork--pattern-${patternVariant}`]"
        :style="artworkStyle"
        aria-hidden="true"
    />
</template>

<style scoped>
.entity-artwork {
    --entity-artwork-angle: 0deg;
    --entity-artwork-flip-x: 1;
    --entity-artwork-flip-y: 1;
    --entity-artwork-scale: 1.06;
    background: var(--entity-artwork-light-background);
}

.entity-artwork--pattern-2 {
    --entity-artwork-angle: 90deg;
    --entity-artwork-scale: 1.1;
}

.entity-artwork--pattern-3 {
    --entity-artwork-angle: 180deg;
    --entity-artwork-scale: 1.06;
}

.entity-artwork--pattern-4 {
    --entity-artwork-angle: 270deg;
    --entity-artwork-scale: 1.1;
}

.entity-artwork--pattern-5 {
    --entity-artwork-flip-x: -1;
    --entity-artwork-scale: 1.08;
}

.entity-artwork--pattern-6 {
    --entity-artwork-angle: 180deg;
    --entity-artwork-flip-x: -1;
    --entity-artwork-scale: 1.08;
}

.entity-artwork::before {
    position: absolute;
    inset: -3%;
    background-image: var(--brand-entity-artwork-image);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    content: '';
    mix-blend-mode: multiply;
    opacity: 0.28;
    transform:
        rotate(var(--entity-artwork-angle))
        scale(var(--entity-artwork-flip-x), var(--entity-artwork-flip-y))
        scale(var(--entity-artwork-scale));
}

</style>

<style>
.dark .entity-artwork {
    background: var(--entity-artwork-dark-background);
}

.dark .entity-artwork::before {
    filter: invert(1);
    mix-blend-mode: screen;
    opacity: 0.24;
}
</style>
